import classNames from "classnames";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Divider } from "components/design/Divider";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { AnimatedNumbers } from "components/DesignSystem/AnimatedNumbers/AnimatedNumbers";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { LockedDatePicker } from "components/LockedDatePicker/LockedDatePicker";
import { DoubleGreenTick } from "components/icons/DoubleGreenTick";
import { BillAllocationRow } from "components/LinkInkleInvoiceModal/BillAllocationRow";
import { SuggestedInvoicesAccordion } from "components/LinkInkleInvoiceModal/SuggestedInvoicesAccordion";
import {
  InvoiceAmountColumn,
  InvoiceDateColumn,
  InvoiceNumberColumn,
  SentToColumn,
} from "components/InvoiceColumns/InvoiceColumns";
import {
  Delete,
  Description,
  Vendor,
} from "components/JournalEntry/AddJournalEntryTable";
import { PriceInput } from "components/PriceInput/PriceInput";
import { RadioCard } from "components/RadioCard/RadioCard";
import { InternalTransaction } from "components/Transaction/AddTransaction/AddTransactionManuallyModal";
import { InfoItem } from "components/Transaction/Slider/TransactionSlider";
import {
  EXCHANGE_GAIN_OR_LOSS_IDENTIFIER,
  INCOME_INDENTIFIER,
  WITHHELD_TAX_PAYABLE_IDENTIFIER,
} from "constants/categoryIdentifier";
import { USD } from "constants/currency";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import {
  PAY_PARTIALLY,
  RESOLVE_DIFFERENCE,
  WRITE_OFF_REMAINING,
} from "constants/invoiceResolveOptions";
import { WriteOffRemainingOption } from "components/InvoiceAction/WriteOffRemainingOption";
import dayjs from "dayjs";
import { Field, FieldProps, Form, Formik, useFormikContext } from "formik";
import { linkInkleSchema } from "formValidations/linkInvoiceSchema";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import randomBytes from "randombytes";
import { Fragment, ReactNode, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  CellContext,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import EmptyInvoices from "static/images/EmptyInvoices.svg";
import {
  useGetAllInvoicesQuery,
  useGetEntityInvoiceQuery,
  useLazyGetAllInvoiceCustomersListQuery,
} from "store/apis/invoices";
import {
  useGetMappedInvoiceQuery,
  useGetTransactionDataQuery,
  useMapInkleTransactionToInvoiceMutation,
} from "store/apis/transactions";
import { useGetBillOrRequestQuery } from "store/apis/vendorBills";
import { Transactions } from "types/Models/books";
import { GroupInvoiceCustomer, Invoice } from "types/Models/invoice";
import { VendorBill } from "types/Models/vendorBills";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { currency } from "utils/Currency";
import { debounce } from "utils/debouncing";
import { flattenTypes } from "utils/flattenCOA";
import { formatDate } from "utils/formatDate";
import { getDateRange } from "utils/getDateRange";
import { getUsdPendingAmount } from "utils/getUsdPendingAmount";
import { pluralize } from "utils/pluralize";
import { roundToFixedDecimal } from "utils/roundtoFixedDecimal";

const EMPTY_SELECTED_BILLS: VendorBill[] = [];

const MODAL_BODY_LOADER = (
  <div className="t-h-60 t-flex t-justify-center t-items-center">
    <Loader size="small" />
  </div>
);

type LinkedInvoicesFormikValues = {
  transactions: InternalTransaction[];
  max_amount?: string;
  min_amount?: string;
  search?: string;
  resolve_difference?: boolean;
  linked_transactions?: InternalTransaction[];
  linked_invoices?: Invoice[];
  is_edit_modal?: boolean;
  show_all_filters?: boolean;
  pay_partially?: boolean;
  write_off_remaining?: boolean;
  write_off_date?: string;
};

const TableHeaderLabel = ({
  children,
  required = false,
}: {
  children: ReactNode;
  required?: boolean;
}) => (
  <span
    className={classNames({
      "after:t-font-bold after:t-text-red-50 after:t-content-['_*']": required,
    })}
  >
    {children}
  </span>
);

const AmountColumn = (info: CellContext<InternalTransaction, number>) => {
  const id = info.row.id;

  return (
    <PriceInput
      rightAlign
      aria-label={`amount input`}
      name={`transactions[${id}].amount`}
      hideError
      placeholder="Enter amount"
      customSize="small"
    />
  );
};

const CustomerFilter = ({
  updateFilter,
  filterValues,
}: {
  updateFilter: <S extends "CUSTOMERS">(
    name: S,
    newValue: {
      CUSTOMERS: string[];
    }[S]
  ) => void;
  filterValues: {
    CUSTOMERS: string[];
  };
}) => {
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [customerSearchValue, setCustomerSearchValue] = useState("");

  const {
    data: invoiceCustomers,
    loadNext,
    isLoading,
    isSuccess,
  } = usePaginatedQuery<{
    client_customers?: GroupInvoiceCustomer[];
  }>(useLazyGetAllInvoiceCustomersListQuery, "client_customers", {
    groupId: group.uuid,
    entityId: entityId,
    searchTerm: customerSearchValue,
  });

  const updateFilterCallback =
    (name: Parameters<typeof updateFilter>[0]) =>
    (newValues: Parameters<typeof updateFilter>[1]) => {
      updateFilter(name, newValues);
    };

  const onCustomerSearch = debounce((value) => setCustomerSearchValue(value));

  if (isLoading && !isSuccess) {
    return (
      <div className="t-w-full t-h-full t-flex t-justify-center t-align-middle">
        <Loader size="small" />
      </div>
    );
  }

  if (invoiceCustomers?.client_customers) {
    return (
      <MultiSelectFilter
        onSearchValueChange={onCustomerSearch}
        hasMore={true}
        totalCount={invoiceCustomers?.client_customers.length}
        loadNext={loadNext}
        onChange={updateFilterCallback("CUSTOMERS")}
        options={invoiceCustomers?.client_customers.map(({ name, uuid }) => ({
          value: uuid,
          label: name,
        }))}
        selected={filterValues.CUSTOMERS}
      />
    );
  }

  return null;
};

export const InvoiceFilter = ({
  currentPage,
  setCurrentPage,
  filterValues,
  updateFilter,
  paginationData,
}: {
  currentPage: number;
  setCurrentPage: (v: number) => void;
  filterValues: {
    START_DATE: string;
    END_DATE: string;
    SELECT_PERIOD: string;
    CUSTOMERS: string[];
  };
  updateFilter: <
    S extends "START_DATE" | "END_DATE" | "SELECT_PERIOD" | "CUSTOMERS"
  >(
    name: S,
    newValue: {
      START_DATE: string;
      END_DATE: string;
      SELECT_PERIOD: string;
      CUSTOMERS: string[];
    }[S]
  ) => void;
  paginationData: {
    totalPage: number;
    currentPage: number;
    itemsPerPage: number;
    totalItemCount: number;
  };
}) => {
  const {
    values: { max_amount, min_amount },
    setFieldValue,
  } = useFormikContext<LinkedInvoicesFormikValues>();

  const appliedFilterCount = [
    filterValues.START_DATE,
    filterValues.CUSTOMERS.length > 0,
    max_amount,
    min_amount,
  ].filter(Boolean).length;
  const filterTitle =
    appliedFilterCount > 0 ? `Filters (${appliedFilterCount})` : "Filters";

  return (
    <>
      <Filter.Root
        withWrapper
        defaultValue="DATE"
        title={filterTitle}
        capsule={
          <span className="t-contents sm:t-hidden">
            {filterValues.START_DATE && (
              <Chip
                onRemove={() => {
                  updateFilter("START_DATE", "");
                  updateFilter("END_DATE", "");
                  updateFilter("SELECT_PERIOD", "");
                }}
                isActive
                filterType="DATE"
              >
                From: {formatDate(filterValues.START_DATE)} To:{" "}
                {formatDate(filterValues.END_DATE)}
              </Chip>
            )}
            {filterValues.CUSTOMERS.length > 0 && (
              <Chip
                onRemove={() => {
                  updateFilter("CUSTOMERS", []);
                }}
                isActive
                filterType="CUSTOMERS"
              >
                Customers ({filterValues.CUSTOMERS.length})
              </Chip>
            )}
            {max_amount && (
              <Chip
                onRemove={() => {
                  setFieldValue("max_amount", null);
                }}
                isActive
                filterType="AMOUNT"
              >
                Max. Amount {currency({ amount: max_amount })}
              </Chip>
            )}
            {min_amount && (
              <Chip
                onRemove={() => {
                  setFieldValue("min_amount", null);
                }}
                isActive
                filterType="AMOUNT"
              >
                Min. Amount {currency({ amount: min_amount })}
              </Chip>
            )}
          </span>
        }
      >
        <Filter.Portal>
          <Filter.List>
            <Filter.ListItem value="DATE">Date</Filter.ListItem>
            <Filter.ListItem value="CUSTOMER">Customer</Filter.ListItem>
            {/* <Filter.ListItem value="AMOUNT">Amount</Filter.ListItem> */}
          </Filter.List>

          <Filter.Body value="DATE" block>
            <BooksDateFilter
              values={filterValues}
              updateFilter={updateFilter}
            />
          </Filter.Body>
          <Filter.Body value="CUSTOMER" block>
            <CustomerFilter
              updateFilter={updateFilter}
              filterValues={filterValues}
            />
          </Filter.Body>
          <Filter.Body value="AMOUNT" block>
            <div
              className="t-flex t-flex-col t-gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              <PriceInput name="min_amount" label="Min. Amount" type="number" />
              <PriceInput name="max_amount" label="Max. Amount" type="number" />
            </div>
          </Filter.Body>
        </Filter.Portal>
      </Filter.Root>
      <div className="t-ml-auto">
        <PaginationHandler
          page_num={currentPage}
          per_page={paginationData.itemsPerPage}
          total_count={paginationData.totalItemCount}
          total_pages={paginationData.totalPage}
          onPageNumChange={setCurrentPage}
        />
      </div>
    </>
  );
};

export const DiscountTransactions = ({
  type = "INVOICE",
  invoiceId,
  pendingAmount,
  selectedBills = EMPTY_SELECTED_BILLS,
}: {
  type?: "BILL" | "INVOICE";
  invoiceId?: string;
  pendingAmount?: number;
  selectedBills?: VendorBill[];
}) => {
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext()?.uuid;
  const { isMobile } = useWindowSize();
  const { data: invoiceData } = useGetEntityInvoiceQuery(
    {
      entityId,
      invoiceId: invoiceId!,
      groupId,
    },
    { skip: !invoiceId || type !== "INVOICE" }
  );

  const { data: bill } = useGetBillOrRequestQuery(
    {
      entityId,
      billId: invoiceId!,
      groupId,
    },
    { skip: !invoiceId || type !== "BILL" }
  );

  const { values, setFieldValue } =
    useFormikContext<LinkedInvoicesFormikValues>();

  const addAnotherTransaction = () => {
    const newValues = [
      ...values.transactions,
      {
        id: randomBytes(10).toString("hex"),
        ...initialTransaction,
        date: values.transactions[0]?.date || "",
      },
    ];
    setFieldValue("transactions", newValues);
  };

  const createColumn = createColumnHelper<InternalTransaction>();

  const { chartOfAccounts = [] } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });

  const flattenedTypes = flattenTypes({
    accounts: chartOfAccounts,
  });

  const incomeCategory = flattenedTypes.find(
    ({ identifier }) => identifier === INCOME_INDENTIFIER
  );
  const exchangeLossOrGain = flattenedTypes.find(
    ({ identifier }) => identifier === EXCHANGE_GAIN_OR_LOSS_IDENTIFIER
  );
  const withheldTaxPayableCategory = flattenedTypes.find(
    ({ identifier }) => identifier === WITHHELD_TAX_PAYABLE_IDENTIFIER
  );

  const isResolvedAsWithholding = values.transactions?.some(
    (transaction) => transaction.category === withheldTaxPayableCategory?.uuid
  );

  const currency = invoiceData?.currency || bill?.currency || USD;

  const defaultCategoryUuid =
    currency === USD ? incomeCategory?.uuid : exchangeLossOrGain?.uuid;

  const showBillSelector = type === "BILL" && selectedBills.length > 1;

  const showWithholdingResolvedMessage =
    type === "BILL" && isResolvedAsWithholding;
  let resolvedMessage = "Resolved";
  let resolvedMessageClassName =
    "t-text-body-sm-italic t-italic t-text-text-30";
  if (showWithholdingResolvedMessage) {
    resolvedMessage =
      "Difference resolved as Withheld tax payable - this is the amount owed to the IRS";
    resolvedMessageClassName = "t-text-body-sm-italic t-italic t-text-green-80";
  }

  const columns = useMemo(
    () => [
      createColumn.accessor("date", {
        size: 16.63,
        header: () => <TableHeaderLabel>Date</TableHeaderLabel>,
        cell: (info) => {
          const name = `transactions[${info.row.index}].date`;
          const onDateChange = (date: Date) => {
            setFieldValue(name, date ? dayjs(date).format(DISPLAY_DATE) : null);
          };

          return (
            <Field name={name}>
              {({ field }: FieldProps) => (
                <LockedDatePicker
                  {...field}
                  hideError
                  customSize="small"
                  placeholder={DISPLAY_DATE}
                  name={name}
                  onDateChange={onDateChange}
                  maxDate={new Date()}
                />
              )}
            </Field>
          );
        },
      }),

      createColumn.accessor("amount", {
        size: 16.63,
        header: () => (
          <div className="t-flex t-justify-end">
            <TableHeaderLabel required>Amount</TableHeaderLabel>
          </div>
        ),
        cell: AmountColumn,
      }),

      createColumn.accessor("description", {
        size: 20.82,
        header: () => <TableHeaderLabel required>Description</TableHeaderLabel>,
        //@ts-ignore
        cell: Description,
      }),

      createColumn.accessor("merchant", {
        size: 20.82,
        header: () => <TableHeaderLabel>To/ From</TableHeaderLabel>,
        cell: (info) => {
          const id = info.row.id;
          const { merchant = "" } = info.row.original || {};

          return <Vendor id={id} merchant={merchant} />;
        },
      }),

      createColumn.accessor("category", {
        size: 20.82,
        header: () => <TableHeaderLabel required>Category</TableHeaderLabel>,
        cell: (info) => {
          const id = info.row.index;
          const previousCategory = info.row.original.category;

          const onCategoryChange = (value: { value: string } | null) => {
            if (!showBillSelector) return;

            const leavingWithheld =
              previousCategory === withheldTaxPayableCategory?.uuid &&
              value?.value !== withheldTaxPayableCategory?.uuid;

            // Clear a stale bill pick if the row is recategorized away from
            // Withheld tax payable, so it can't leak into submission.
            if (leavingWithheld) {
              setFieldValue(`transactions[${id}].withheldBillId`, undefined);
            }
          };

          return (
            <CategorySelector
              name={`transactions[${id}].category`}
              withForm
              size="small"
              category={
                info.row.original.category || defaultCategoryUuid || null
              }
              hiddenCategoryTypes={["BANK_ACCOUNT"]}
              onChange={onCategoryChange}
            />
          );
        },
      }),

      createColumn.accessor("id", {
        size: 4.28,
        header: "",
        cell: (info) => {
          return (
            <Delete
              disabled={info.table.options.data.length === 1}
              id={info.getValue().toString()}
            />
          );
        },
      }),
    ],
    [
      values,
      defaultCategoryUuid,
      showBillSelector,
      selectedBills,
      withheldTaxPayableCategory,
      setFieldValue,
    ]
  );

  const table = useReactTable({
    data: values.transactions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
      maxSize: 100,
    },
  });

  return (
    <div className="t-text-body-sm t-text-text-60 t-flex t-flex-col t-gap-4">
      {values?.resolve_difference && (
        <>
          {values?.transactions?.length > 0 && (
            <TableUI
              table={table}
              contentClassName="sm:t-min-w-[760px]"
              layout="fixed"
              isHeaderCellUpperCase={false}
              children={
                showBillSelector
                  ? ({ row }) => {
                      const showBillAllocationRow =
                        row.original.category ===
                        withheldTaxPayableCategory?.uuid;

                      return (
                        <Fragment key={row.id}>
                          <Table.Row
                            key={row.id}
                            className={classNames({
                              "!t-border-0": showBillAllocationRow,
                            })}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <Table.Cell
                                key={cell.id}
                                style={{ width: `${cell.column.getSize()}%` }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Table.Cell>
                            ))}
                          </Table.Row>
                          {showBillAllocationRow && (
                            <Table.Row key={`${row.id}-bill-allocation`}>
                              <Table.Cell
                                colSpan={row.getVisibleCells().length}
                                className="!t-py-0"
                              >
                                <BillAllocationRow
                                  rowIndex={row.index}
                                  selectedBills={selectedBills}
                                />
                              </Table.Cell>
                            </Table.Row>
                          )}
                        </Fragment>
                      );
                    }
                  : undefined
              }
              moreRows={
                <>
                  <Table.Row className="!t-border-0">
                    <Table.Cell colSpan={columns.length}>
                      <Button
                        type="button"
                        size={isMobile ? "small" : "regular"}
                        onClick={addAnotherTransaction}
                      >
                        Add another transaction
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row className="!t-border-0">
                    <Table.Cell colSpan={columns.length} className="t-pt-1">
                      <div className="t-flex t-flex-col t-items-end t-gap-1">
                        <div className="t-flex t-items-center t-gap-3">
                          <span className="t-text-subtext">
                            Amount pending:
                          </span>
                          <AnimatedNumbers
                            amount={Number(pendingAmount) || 0}
                          />
                        </div>

                        {pendingAmount === 0 && (
                          <div className="t-flex t-gap-2 t-items-center">
                            <span className="t-text-green-80">
                              <DoubleGreenTick />
                            </span>
                            <span className={resolvedMessageClassName}>
                              {resolvedMessage}
                            </span>
                          </div>
                        )}

                        {Boolean(pendingAmount) && pendingAmount! > 0 && (
                          <span className="t-text-body-italic t-italic t-text-text-30 sm:t-text-body-sm-italic">
                            Difference is not fully resolved, invoice will be
                            marked as partially paid
                          </span>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                </>
              }
            />
          )}
        </>
      )}
    </div>
  );
};

const LinkInkleInvoiceBody = ({
  transaction: { transaction, available_amount: pending_amount },
  selectedInvoices,
  setSelectedInvoices,
  resolvePartialPaymentStep,
  existingAdjustmentAmount,
}: {
  transaction: Transactions;
  selectedInvoices: Invoice[];
  setSelectedInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>;
  resolvePartialPaymentStep: boolean;
  existingAdjustmentAmount?: number;
}) => {
  const history = useHistory();
  const { search } = useLocation();
  const group = useCurrentGroupContext();
  const { isMobile } = useWindowSize();
  const entityId = useCurrentEntityId();
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCurrency, setDisplayCurrency] = useState<
    "INVOICE_CURRENCY" | "USD"
  >("USD");

  const { values: formikValues, setFieldValue } =
    useFormikContext<LinkedInvoicesFormikValues>();

  const defaultDateRange = getDateRange("last90days");
  const { values: filterValues, updateFilter } = useFilters({
    initialValue: {
      START_DATE: dayjs(defaultDateRange.startDate).format(DISPLAY_DATE),
      END_DATE: dayjs(defaultDateRange.endDate).format(DISPLAY_DATE),
      SELECT_PERIOD: "last90days",
      CUSTOMERS: [] as string[],
    },
  });

  const { chartOfAccounts = [] } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });
  const flattenedTypes = flattenTypes({
    accounts: chartOfAccounts,
  });

  const incomeCategory = flattenedTypes.find(
    ({ identifier }) => identifier === 41000
  );
  const exchangeLossOrGain = flattenedTypes.find(
    ({ identifier }) => identifier === 53030
  );

  const [sorting, setSorting] = useState<
    {
      desc: boolean;
      id: "CREATED_AT" | "SENT_TO" | "AMOUNT";
    }[]
  >([]);

  const sortCol = sorting[0]?.id as
    | "INVOICE_DATE"
    | "DUE_DATE"
    | "SENT_TO"
    | "AMOUNT"
    | undefined;
  const sortOrder = sorting[0]?.desc;

  const {
    data: allInvoices,
    isLoading,
    isSuccess,
  } = useGetAllInvoicesQuery(
    {
      currentPage: currentPage,
      groupId: group?.uuid!,
      entityId: entityId,
      searchTerm: formikValues?.search || null,
      startDate: filterValues.START_DATE
        ? dayjs(filterValues.START_DATE).format(YYYY_MM_DD)
        : undefined,
      endDate: filterValues.END_DATE
        ? dayjs(filterValues.END_DATE).format(YYYY_MM_DD)
        : undefined,
      customerIds: filterValues.CUSTOMERS.join(","),
      minAmount: formikValues?.min_amount,
      maxAmount: formikValues?.max_amount,
      sortCol: sortCol,
      sortOrder: sortOrder ? ("DSC" as const) : ("ASC" as const),
      limit: 5,
      linkedStatus: "UNLINKED",
    },
    {
      skip: !group?.uuid || !entityId,
      refetchOnMountOrArgChange: true,
    }
  );

  const { total_pages = 0, invoices = [] } = allInvoices || {};

  const paginationData = {
    totalPage: allInvoices?.total_pages!,
    currentPage: allInvoices?.current_page!,
    itemsPerPage: allInvoices?.per_page!,
    totalItemCount: allInvoices?.total_count!,
  };

  const data = useMemo(() => invoices, [invoices]);

  const createColumn = createColumnHelper<Invoice>();

  const invoiceColumns = [
    createColumn.display({
      id: "select",
      size: 1,
      enableSorting: false,
      header: () => <></>,

      cell: ({ row }) => {
        const isDisabled = !row.getCanSelect();

        return (
          <ConditionalToolTip
            condition={
              isDisabled
                ? "The total amount of selected invoices has already exceeded the transaction amount"
                : false
            }
          >
            <span>
              <Checkbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: isDisabled,
                  indeterminate: row.getIsSomeSelected(),
                  key: row.id,
                }}
              />
            </span>
          </ConditionalToolTip>
        );
      },
    }),

    InvoiceDateColumn,
    InvoiceNumberColumn,
    { header: "", cell: () => <></>, id: "EMPTY", size: 10 },
    SentToColumn,
    { ...InvoiceAmountColumn, size: 26 },
  ];

  const transactionAmount =
    formikValues.transactions.reduce((acc, { amount }) => {
      return (acc = acc + Number(amount));
    }, 0) + (existingAdjustmentAmount || 0);

  const invoicesTotalAmount = selectedInvoices.reduce(
    (acc, invoice) => acc + getUsdPendingAmount(invoice),
    0
  );

  const isAnySelectedInvoiceMultiCurrency = selectedInvoices.some(
    ({ payment_record_amount }) =>
      payment_record_amount?.exchange_currency &&
      payment_record_amount?.exchange_currency !== USD
  );

  const pendingAmount =
    roundToFixedDecimal({
      numberToRound: pending_amount || transaction.amount,
    }) -
    (invoicesTotalAmount + transactionAmount);

  const pendingAmountWithoutManual = pendingAmount + transactionAmount;

  const enableSelection = pendingAmount > 0;

  const table = useReactTable({
    data,
    columns: invoiceColumns,
    state: {
      rowSelection: selectedInvoices.reduce((accumulator, { uuid }) => {
        return { ...accumulator, [uuid]: true };
      }, {}),
      sorting,
    },
    enableRowSelection: (row) => {
      return row.getIsSelected() ? true : enableSelection;
    },

    getRowId: (row) => row.uuid,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    //@ts-ignore
    onSortingChange: setSorting,
    enableRowPinning: true,
    meta: {
      displayCurrency,
      setDisplayCurrency,
      showPendingAmount: true,
    },
  });

  const onRowClick = (row: Row<Invoice>) => {
    if (!row.getCanSelect()) {
      return;
    }

    const isRowSelected = selectedInvoices.find(
      (txn) => txn.uuid === row.original.uuid
    );

    if (isRowSelected) {
      return setSelectedInvoices((prev) =>
        prev.filter((txn) => txn.uuid !== row.original.uuid)
      );
    }

    setSelectedInvoices((prev) => [...prev, row.original]);
  };

  const handleSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFieldValue("search", value);
  });

  if (isLoading && !isSuccess) {
    return (
      <div className="t-w-full t-h-full t-flex t-justify-center t-align-middle">
        <Loader size="small" />
      </div>
    );
  }

  let resolveValue;

  if (formikValues.resolve_difference) {
    resolveValue = RESOLVE_DIFFERENCE;
  }

  if (formikValues.pay_partially) {
    resolveValue = PAY_PARTIALLY;
  }

  if (formikValues.write_off_remaining) {
    resolveValue = WRITE_OFF_REMAINING;
  }

  const onResolveValueChange = (value: string) => {
    setFieldValue("resolve_difference", value === RESOLVE_DIFFERENCE);
    setFieldValue("pay_partially", value === PAY_PARTIALLY);
    setFieldValue("write_off_remaining", value === WRITE_OFF_REMAINING);

    const transactionDate = transaction.date
      ? dayjs(transaction.date).format(DISPLAY_DATE)
      : "";

    if (value === PAY_PARTIALLY || value === WRITE_OFF_REMAINING) {
      setFieldValue("transactions", [
        {
          ...initialTransaction,
          id: randomBytes(10).toString("hex"),
          date: transactionDate,
        },
      ]);
    }
    const exchangeGainOrLoss =
      pendingAmountWithoutManual > 0 ? "Exchange Gain" : "Exchange Loss";

    const incomeOrExpense =
      pendingAmountWithoutManual > 0 ? "Income" : "Expense";

    if (value === RESOLVE_DIFFERENCE) {
      const defaultCategory = isAnySelectedInvoiceMultiCurrency
        ? exchangeLossOrGain
        : incomeCategory;

      setFieldValue("transactions", [
        {
          ...initialTransaction,
          id: randomBytes(10).toString("hex"),
          date: transactionDate,
          description:
            defaultCategory?.name ||
            (isAnySelectedInvoiceMultiCurrency
              ? exchangeGainOrLoss
              : incomeOrExpense),
          category: defaultCategory?.uuid || "",
          amount: pendingAmount,
        },
      ]);
    }
  };
  const overpayment = pendingAmountWithoutManual < 0;
  const differenceAmount = Math.abs(pendingAmountWithoutManual);
  const differenceCategory = overpayment ? "expense" : "income";
  const differenceLabel = overpayment ? "an expense" : "income";
  const latestInvoiceDate = selectedInvoices
    .map(({ invoice_date }) => invoice_date)
    .filter(Boolean)
    .sort()
    .at(-1);

  return (
    <div className="t-flex t-flex-col t-gap-6">
      <div className="t-border-solid t-border t-border-neutral-0 t-py-4 t-px-5 t-bg-white t-flex t-gap-8 t-rounded-lg sm:t-grid sm:t-grid-cols-2 sm:t-gap-4 sm:t-px-4">
        <InfoItem label="Date">{formatDate(transaction.date)}</InfoItem>

        <Divider orientation="vertical" classNames="sm:!t-hidden" />

        <InfoItem label="Source">
          <ToolTip text={transaction.from?.bank_account?.bank_brand?.name}>
            <Avatar
              src={transaction.from?.bank_account?.bank_brand?.logo_url}
              alt={transaction.from?.bank_account?.bank_brand?.name || "Bank"}
            />
          </ToolTip>
        </InfoItem>
        {transaction.merchant && (
          <>
            <Divider orientation="vertical" classNames="sm:!t-hidden" />

            <InfoItem label="To/From">{transaction.merchant}</InfoItem>
          </>
        )}

        <div className="t-ml-auto sm:t-ml-0">
          <InfoItem label="Available amount to link">
            <div className="t-text-right sm:t-text-left">
              <AmountSuperScript amount={Number(transaction.amount) || 0} />
            </div>
          </InfoItem>
        </div>
      </div>
      {resolvePartialPaymentStep ? (
        <div>
          <p className="t-m-0 t-text-body-sm t-mb-6 t-text-text-100">
            The available transaction amount of{" "}
            <span className="t-font-bold">
              <AmountSuperScript amount={transaction.amount} />
            </span>{" "}
            {overpayment ? (
              <span>
                is{" "}
                <span className="t-font-bold">
                  <AmountSuperScript
                    amount={Math.abs(pendingAmountWithoutManual)}
                  />
                </span>{" "}
                less than the selected invoice(s)
              </span>
            ) : (
              <span>
                exceeds the selected invoice(s) by{" "}
                <span className="t-font-bold">
                  <AmountSuperScript
                    amount={Math.abs(pendingAmountWithoutManual)}
                  />
                </span>{" "}
              </span>
            )}
            {isAnySelectedInvoiceMultiCurrency && (
              <span>. This difference may be due to exchange rate changes</span>
            )}
            . How would you like to handle the difference?
          </p>

          <RadioGroup.Root
            value={resolveValue}
            name="resolve_difference"
            onValueChange={onResolveValueChange}
          >
            <RadioGroup.Content className="t-flex t-flex-col t-gap-5">
              <RadioCard value={RESOLVE_DIFFERENCE}>
                <div className="t-text-body-sm t-flex t-flex-col t-gap-0.5">
                  <span>Categorise as {differenceCategory}</span>
                  <p className="t-text-text-30 t-m-0 t-italic t-text-body-italic sm:t-text-body-sm-italic">
                    Marks the invoice(s) Paid and records the{" "}
                    <AmountSuperScript amount={differenceAmount} /> difference
                    as{" "}
                    {isAnySelectedInvoiceMultiCurrency
                      ? `an exchange gain/loss or ${differenceCategory}`
                      : differenceLabel}
                    .
                  </p>
                </div>
              </RadioCard>
              {formikValues.resolve_difference && (
                <div className="t-border-solid t-border-neutral-10 t-border t-pt-1 t-p-3 t-rounded-md">
                  <DiscountTransactions pendingAmount={pendingAmount} />
                </div>
              )}
              <RadioCard value={PAY_PARTIALLY}>
                <div className="t-text-body-sm t-flex t-flex-col t-gap-0.5">
                  <span>
                    {overpayment
                      ? "Mark invoice(s) partially paid"
                      : "Partially link to invoice(s)"}
                  </span>
                  <p className="t-text-text-30 t-m-0 t-italic t-text-body-italic sm:t-text-body-sm-italic">
                    Apply the {overpayment ? "transaction" : "invoice"} amount
                    only. The remaining{" "}
                    <AmountSuperScript
                      amount={Math.abs(pendingAmountWithoutManual)}
                    />{" "}
                    {overpayment
                      ? "stays due on the invoice(s)."
                      : "will stay unallocated as 'Unapplied Cash Payment Income'."}
                  </p>
                </div>
              </RadioCard>
              {overpayment && (
                <WriteOffRemainingOption
                  amount={differenceAmount}
                  isSelected={Boolean(formikValues.write_off_remaining)}
                  invoiceDate={latestInvoiceDate}
                  invoiceCount={selectedInvoices.length}
                />
              )}
            </RadioGroup.Content>
          </RadioGroup.Root>
        </div>
      ) : (
        <>
          <SuggestedInvoicesAccordion
            transactionId={transaction.uuid}
            selectedInvoices={selectedInvoices}
            setSelectedInvoices={setSelectedInvoices}
            enableSelection={enableSelection}
          />
          <div className="t-flex t-gap-2 t-flex-wrap">
            <Search
              className="t-w-[12%] focus:t-w-2/6 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
              placeholder="Search..."
              onChange={handleSearch}
            />

            <InvoiceFilter
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              filterValues={filterValues}
              updateFilter={updateFilter}
              paginationData={paginationData}
            />
          </div>
          {invoices.length === 0 && !formikValues.is_edit_modal ? (
            <div className="t-w-full t-h-full t-flex t-flex-col t-justify-center t-items-center t-py-12 t-gap-2">
              <img src={EmptyInvoices} alt="EmptyInvoices" />
              <div className="t-w-full t-h-full t-flex t-flex-col t-justify-center t-items-center t-gap-3">
                <div className="t-text-body-sm t-text-text-30 sm:t-text-center">
                  There are no invoices that are sent and not paid yet
                </div>
                <Button
                  customType="primary"
                  size={isMobile ? "small" : "regular"}
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(`/books/invoices-and-customers${search}`);
                  }}
                >
                  Create Invoice
                </Button>
              </div>
            </div>
          ) : (
            <TableUI
              table={table}
              onRowClick={onRowClick}
              moreRows={
                <Table.Row className="!t-border-0">
                  <Table.Cell colSpan={4} />
                  <Table.Cell className="t-text-subtext t-text-text-60">
                    Amount Pending
                  </Table.Cell>
                  <Table.Cell className="t-text-right t-text-subtext t-text-text-60">
                    <AnimatedNumbers amount={Number(pendingAmount) || 0} />
                  </Table.Cell>
                </Table.Row>
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export const initialTransaction: Omit<
  InternalTransaction,
  "id" | "date" | "invoice"
> = {
  merchant: "",
  category: "",
  description: "",
  amount: 0,
};

export const LinkInkleInvoiceModal = ({
  transactionId,
  isOpen,
  close,
}: {
  transactionId: string;
} & ModalProps) => {
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext().uuid;
  const { alertToast, successToast } = useToast();
  const [markInvoicePaid, { isLoading }] =
    useMapInkleTransactionToInvoiceMutation();
  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);

  const { data: linkedTxnData } = useGetMappedInvoiceQuery({
    entityId,
    transactionId,
  });

  const {
    data: transaction,
    isLoading: isTransactionLoading,
    isSuccess: isTransactionSuccess,
  } = useGetTransactionDataQuery(
    {
      entityId,
      transactionId,
      groupId,
    },
    { skip: !transactionId || !entityId }
  );

  const initialTransactions = useMemo(
    () =>
      Array.from({ length: 1 }).map(() => ({
        id: randomBytes(10).toString("hex"),
        ...initialTransaction,
      })),
    []
  );

  const initialValues = {
    transactions: initialTransactions,
    resolve_difference: false,
    pay_partially: false,
    write_off_remaining: false,
    write_off_date: dayjs().format(YYYY_MM_DD),
  };

  const partialPaymentStep = useModal();

  const onSubmit = async (values: {
    pay_partially?: boolean;
    resolve_difference?: boolean;
    write_off_remaining?: boolean;
    write_off_date?: string;
    transactions: typeof initialTransactions;
  }) => {
    try {
      const entityInvoiceIds = selectedInvoices
        .map(({ uuid }) => uuid)
        .join(",");

      const availableAmount =
        transaction?.available_amount || transaction?.transaction.amount || 0;

      const totalInvoiceAmount = selectedInvoices.reduce(
        (acc, invoice) => acc + getUsdPendingAmount(invoice),
        0
      );

      const partiallyPaying = totalInvoiceAmount < availableAmount;
      const overpayment = totalInvoiceAmount > availableAmount;

      const transactions = values.resolve_difference
        ? values.transactions.map(
            ({ amount, description, category, merchant }) => {
              return {
                amount: overpayment ? -amount : amount,
                description: description,
                category_id: category,
                merchant_id: merchant,
              };
            }
          )
        : null;

      if (!partialPaymentStep.isOpen && (partiallyPaying || overpayment)) {
        return partialPaymentStep.open();
      }

      await markInvoicePaid({
        entityId,
        entityInvoiceIds,
        transactionId,
        ...(transactions && { transactions }),
        ...(values.write_off_remaining && {
          writeOffRemainingAt: values.write_off_date,
        }),
      }).unwrap();

      let successMessage = `Transaction linked to ${pluralize(
        selectedInvoices.length,
        "invoice",
        "invoices"
      )}`;

      if (values.pay_partially || values.resolve_difference) {
        successMessage = "Partial payment recorded successfully";
      }

      if (values.write_off_remaining) {
        successMessage = "Payment recorded and remaining amount written off";
      }

      successToast({ message: successMessage });
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues}>
      {({ isSubmitting, submitForm, setFieldValue, values }) => {
        const isResolveDifferenceValid = values.resolve_difference
          ? linkInkleSchema.isValidSync(values)
          : true;

        return (
          <Form>
            <Modal.Root open={isOpen} onOpenChange={close}>
              <Modal.Content size="xxl" useCustomOverlay>
                <Modal.Header className="sm:t-pl-4">
                  <Modal.Title>Link to invoices</Modal.Title>
                  <Modal.Close disabled={isLoading} />
                </Modal.Header>
                <Modal.Body className="sm:t-px-4">
                  <Async.Root
                    isLoading={isTransactionLoading}
                    isSuccess={isTransactionSuccess}
                    isEmpty={false}
                    customLoader={MODAL_BODY_LOADER}
                  >
                    <Async.Success>
                      {transaction && (
                        <LinkInkleInvoiceBody
                          resolvePartialPaymentStep={partialPaymentStep.isOpen}
                          transaction={transaction}
                          selectedInvoices={selectedInvoices}
                          setSelectedInvoices={setSelectedInvoices}
                          existingAdjustmentAmount={
                            linkedTxnData?.adjustments.fx_data_adjustment
                              .fx_difference ||
                            linkedTxnData?.adjustments.manual_adjustment.amount
                          }
                        />
                      )}
                    </Async.Success>
                  </Async.Root>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  {partialPaymentStep.isOpen ? (
                    <Button
                      customType="secondary"
                      onClick={() => {
                        partialPaymentStep.close();
                        setFieldValue("transactions", initialTransactions);
                        setFieldValue("resolve_difference", false);
                        setFieldValue("pay_partially", false);
                        setFieldValue("write_off_remaining", false);
                      }}
                    >
                      Back
                    </Button>
                  ) : (
                    <Modal.RawClose asChild>
                      <Button>Cancel</Button>
                    </Modal.RawClose>
                  )}
                  <Button
                    customType="primary"
                    isLoading={isSubmitting || isLoading}
                    disabled={
                      isSubmitting ||
                      isLoading ||
                      selectedInvoices.length === 0 ||
                      !isResolveDifferenceValid ||
                      (partialPaymentStep.isOpen &&
                        !values.pay_partially &&
                        !values.resolve_difference &&
                        !values.write_off_remaining) ||
                      (values.write_off_remaining && !values.write_off_date)
                    }
                    onClick={submitForm}
                  >
                    Proceed
                  </Button>
                </Modal.FooterButtonGroup>
              </Modal.Content>
            </Modal.Root>
          </Form>
        );
      }}
    </Formik>
  );
};
